import type { ReactElement, ReactNode } from 'react';
import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { Chip } from '@/atoms/Chip';
import { Sparkline } from '@/atoms/Sparkline';
import { backgroundColors, statusColors } from '@/tokens/colors';
import { radius } from '@/tokens/radius';
import { dsVar } from '@/tokens/surfaceScopes';
import { fontSize, fontWeight } from '@/tokens/typography';

/**
 * Table cell primitives.
 * Classification: custom
 *
 * The row anatomy of an AiStrike list view. A table's quality lives mostly
 * inside its cells, so these ship as components: reach for them from a column's
 * `Cell` renderer instead of assembling Box + Typography and picking colours by
 * hand. Every value here comes from a token.
 */

/* ------------------------------------------------------------------ */
/* TableTitleCell                                                      */
/* ------------------------------------------------------------------ */

export interface TableTitleCellProps {
  /** The record's name. Truncates rather than wrapping. */
  title: string;
  /** Supporting line under the title — a summary, a type, a path. Truncates. */
  description?: string;
  /** Leading glyph, avatar or status icon. Keep it 24-28px. */
  icon?: ReactNode;
  /** Small dim text beside the title, e.g. a version. */
  meta?: string;
  /** Short uppercase tag beside the title, e.g. "Built-in". */
  badge?: string;
}

/**
 * The identity of a row. Use it in the first column of every list view — it is
 * what makes a table scannable, and the single most-reinvented cell in the
 * product.
 */
export function TableTitleCell({ title, description, icon, meta, badge }: TableTitleCellProps) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
      {icon ? (
        <Box
          sx={{
            flexShrink: 0,
            display: 'grid',
            placeItems: 'center',
            width: 26,
            height: 26,
            borderRadius: `${radius.sm + 2}px`,
            backgroundColor: dsVar('tableRowHover', backgroundColors.surface),
            border: `1px solid ${backgroundColors.border}`,
            color: 'text.secondary',
            fontSize: `${fontSize.sm}px`,
            lineHeight: 0,
          }}
        >
          {icon}
        </Box>
      ) : null}

      <Box sx={{ minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
          <Typography variant="subtitle2" component="span" noWrap sx={{ color: 'text.primary' }}>
            {title}
          </Typography>
          {meta ? (
            <Typography
              variant="caption"
              component="span"
              sx={{ color: 'text.disabled', flexShrink: 0 }}
            >
              {meta}
            </Typography>
          ) : null}
          {badge ? (
            <Chip purpose="category" size="small" label={badge} sx={{ flexShrink: 0 }} />
          ) : null}
        </Box>

        {description ? (
          <Typography
            variant="body1"
            component="span"
            noWrap
            sx={{ display: 'block', color: 'text.secondary' }}
          >
            {description}
          </Typography>
        ) : null}
      </Box>
    </Box>
  );
}

/* ------------------------------------------------------------------ */
/* TableStatusCell                                                     */
/* ------------------------------------------------------------------ */

export type TableStatusTone = 'active' | 'warning' | 'error' | 'info' | 'neutral';

const TONE_COLOR: Record<TableStatusTone, string> = {
  active: statusColors.general,
  warning: statusColors.warning,
  error: statusColors.failure,
  info: statusColors.info,
  // Resolved via the theme so light mode does not inherit the dark subtle grey.
  neutral: 'text.secondary',
};

export interface TableStatusCellProps {
  label: string;
  /** Which semantic hue the dot and label take. */
  tone?: TableStatusTone;
}

/**
 * Lifecycle or health state, as a dot and a label.
 *
 * Use this for state (enabled, healthy, failing) and a `Chip` for facets
 * (tags, sources, severity). Mixing them makes a table read as noise.
 */
export function TableStatusCell({ label, tone = 'neutral' }: TableStatusCellProps) {
  const color = TONE_COLOR[tone];
  const lit = tone !== 'neutral';
  return (
    <Typography
      variant="body1"
      component="span"
      noWrap
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '6px',
        fontWeight: fontWeight.medium,
        color,
      }}
    >
      <Box
        component="span"
        aria-hidden
        sx={{
          width: 7,
          height: 7,
          borderRadius: '50%',
          backgroundColor: color,
          boxShadow: lit ? `0 0 6px ${color}` : 'none',
        }}
      />
      {label}
    </Typography>
  );
}

/* ------------------------------------------------------------------ */
/* TableMetricCell                                                     */
/* ------------------------------------------------------------------ */

export interface TableMetricCellProps {
  /** The headline figure, already formatted (e.g. "8.9k"). */
  value: ReactNode;
  /** Optional trend series drawn to the left of the value. */
  trend?: readonly number[];
  /** Accessible description of the trend. Required when `trend` is given. */
  trendLabel?: string;
  /** Full-precision figure shown on hover. */
  tooltip?: string;
  /** Dim the whole cell — for a zero or inactive measure. */
  muted?: boolean;
  /** Right-align, the default for numbers in a column. */
  align?: 'left' | 'right';
}

/** A number that carries a trend. Uses tabular figures so columns line up. */
export function TableMetricCell({
  value,
  trend,
  trendLabel,
  tooltip,
  muted = false,
  align = 'right',
}: TableMetricCellProps) {
  const body = (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        justifyContent: align === 'right' ? 'flex-end' : 'flex-start',
        ...(muted ? { filter: 'grayscale(1)', opacity: 0.6 } : {}),
      }}
    >
      {trend ? (
        <Sparkline
          data={trend}
          variant="area"
          tone="accent"
          showEndPoint
          width={78}
          height={24}
          label={trendLabel ?? 'Trend'}
        />
      ) : null}
      <Typography
        variant="subtitle2"
        component="span"
        sx={{
          color: muted ? 'text.secondary' : 'text.primary',
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {value}
      </Typography>
    </Box>
  );

  return tooltip ? (
    <Tooltip arrow placement="top" title={tooltip}>
      {body}
    </Tooltip>
  ) : (
    body
  );
}

/* ------------------------------------------------------------------ */
/* TableMetaCell                                                       */
/* ------------------------------------------------------------------ */

export interface TableMetaCellProps {
  /** The fact — "10h ago", "Jun 27, 2026". */
  primary: ReactNode;
  /** Who or what it belongs to — an actor, a source, an id. */
  secondary?: ReactNode;
}

/** Incidental metadata on two lines: the fact, then whose it is. */
export function TableMetaCell({ primary, secondary }: TableMetaCellProps) {
  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography
        variant="body1"
        component="span"
        noWrap
        sx={{ display: 'block', color: 'text.secondary' }}
      >
        {primary}
      </Typography>
      {secondary ? (
        <Typography
          variant="caption"
          component="span"
          noWrap
          sx={{ display: 'block', color: 'text.disabled' }}
        >
          {secondary}
        </Typography>
      ) : null}
    </Box>
  );
}

/* ------------------------------------------------------------------ */
/* TableChipsCell                                                      */
/* ------------------------------------------------------------------ */

export interface TableChipsCellProps {
  items: readonly string[];
  /** How many to show before collapsing the rest into a "+N". */
  max?: number;
  /** Leading glyph repeated on every chip. */
  icon?: ReactNode;
}

/** A set of facets that may overflow. Caps at `max` and counts the remainder. */
export function TableChipsCell({ items, max = 3, icon }: TableChipsCellProps) {
  const shown = items.slice(0, max);
  const rest = items.length - shown.length;

  return (
    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', minWidth: 0 }}>
      {shown.map((item) => (
        <Chip
          key={item}
          purpose="category"
          size="small"
          label={item}
          icon={icon as ReactElement | undefined}
        />
      ))}
      {rest > 0 ? (
        // The remainder is disclosed on hover and focus rather than by expanding
        // in place, so a long facet list cannot change the row height. tabIndex
        // is what makes the tooltip reachable without a pointer.
        <Tooltip arrow placement="top" title={items.slice(max).join(', ')}>
          <Chip purpose="metric" size="small" label={`+${rest}`} tabIndex={0} />
        </Tooltip>
      ) : null}
    </Box>
  );
}

/* ------------------------------------------------------------------ */
/* TableActionsCell                                                    */
/* ------------------------------------------------------------------ */

export interface TableActionsCellProps {
  /** IconButtons, usually. Keep it to three or fewer. */
  children: ReactNode;
  /**
   * Fade the actions in on row hover. They stay reachable by keyboard, so this
   * hides visual noise without hiding function.
   */
  revealOnHover?: boolean;
}

/** Right-aligned row actions. */
export function TableActionsCell({ children, revealOnHover = true }: TableActionsCellProps) {
  return (
    <Box
      sx={{
        display: 'flex',
        gap: 0.5,
        justifyContent: 'flex-end',
        ...(revealOnHover
          ? {
              opacity: 0,
              transition: 'opacity .12s ease',
              'tr:hover &, tr:focus-within &, &:focus-within': { opacity: 1 },
            }
          : {}),
      }}
    >
      {children}
    </Box>
  );
}
